import { useEffect, useState } from 'react';
import { Modal, Form, InputNumber, Input } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { createTransaction, clearFlags } from '../../../store/slices/transactionSlice';
import { CustomButton } from '../CustomButton';
import { CustomInput } from '../CustomInput';
import CustomSelect from '../CustomSelect';
import CustomDatePicker from '../CustomDatePicker';
import FileUpload from '../FileUpload';
import Colors from '../../../utils/colors';
import { showToast } from '../../../utils/toast';
import dayjs from 'dayjs';

const CreateTransactionModal = ({ isOpen, onClose, parentTransactionId }) => {
  const [form] = Form.useForm();
  const [selectedFile, setSelectedFile] = useState(null);
  const dispatch = useDispatch();
  const { loading, isCreated, error } = useSelector((state) => state.transaction);

  useEffect(() => {
    if (isCreated) {
      showToast('Transaction created successfully');
      form.resetFields();
      onClose();
      dispatch(clearFlags());
    }

    if (error) {
      showToast(error, 'error');
    }
  }, [isCreated, error, form, onClose, dispatch]);

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        date: dayjs()
      });
    } else {
      form.resetFields();
      setSelectedFile(null);
    }
  }, [isOpen, form]);

  const handleSubmit = (values) => {
    if (!selectedFile) {
      showToast('Please select a file', 'error');
      return;
    }

    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'date' && value) {
        formData.append(key, value.toISOString());
      } else if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
    if (parentTransactionId) {
      formData.append('parentTransactionId', parentTransactionId);
    }
    formData.append('document', selectedFile);
    
    dispatch(createTransaction(formData));
    form.resetFields();
    setSelectedFile(null);
  };

  return (
    <Modal
      title={parentTransactionId ? "Create Installment" : "Create Transaction"}
      open={isOpen}
      onCancel={onClose}
      maskClosable={false}
      centered
      width={600}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: '20px' }}
      >
        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: 'Please enter description' }]}
        >
        <Input.TextArea 
          placeholder="Enter description"
          rows={3}
          maxLength={500}
          showCount
        />
        </Form.Item>

        {!parentTransactionId && (
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item
              name="accountType"
              label="Account Type"
              rules={[{ required: true, message: 'Please select account type' }]}
              style={{ flex: 1 }}
            >
              <CustomSelect 
                placeholder="Select account type"
                options={[
                  { value: 'corporate', label: 'Corporate' },
                  { value: 'escrow', label: 'Escrow' }
                ]}
              />
            </Form.Item>

            <Form.Item
              name="businessStatus"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
              style={{ flex: 1 }}
            >
              <CustomSelect 
                placeholder="Select status"
                options={[
                  { value: 'inhouse', label: 'Inhouse' },
                  { value: 'client', label: 'Client' }
                ]}
              />
            </Form.Item>
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px' }}>
          <Form.Item
            name="total"
            label="Total"
            rules={[{ required: true, message: 'Please enter total' }]}
            style={{ flex: 1 }}
          >
            <InputNumber
              placeholder="0.00"
              precision={2}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select date' }]}
            initialValue={dayjs()}
            style={{ flex: 1 }}
          >
            <CustomDatePicker />
          </Form.Item>
        </div>

        <Form.Item
          name="documentName"
          label="Document Name (Optional)"
        >
          <CustomInput placeholder="Enter document name" />
        </Form.Item>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Upload Document <span style={{ color: Colors.error }}>*</span>
          </label>
          <FileUpload
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={setSelectedFile}
            value={selectedFile}
            placeholder="Select document file"
            description="Supported formats: PDF, DOC, DOCX, JPG, PNG"
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
          <CustomButton
            variant="outline"
            color={Colors.error}
            onClick={onClose}
            disabled={loading}
            width={140}
            height={32}
            fontSize={12}
          >
            Cancel
          </CustomButton>
          <CustomButton
            htmlType="submit"
            disabled={loading}
            width={140}
            height={32}
            fontSize={12}
          >
            Create Transaction
          </CustomButton>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateTransactionModal;